import {
  ChangeEvent,
  DragEvent,
  KeyboardEvent,
  useCallback,
  useRef,
  useState,
} from 'react';
import {
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import CloseIcon from '@material-ui/icons/Close';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import DescriptionIcon from '@material-ui/icons/Description';
import ImageIcon from '@material-ui/icons/Image';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import { Artifact } from '../../api/types';

const ACCEPTED_EXTENSIONS = '.pdf,.docx,.md,.txt,.png,.jpg,.jpeg,.svg';
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_DESCRIPTION_LENGTH = 2000;

function getStartTooltip(
  uploading: boolean,
  disableStart: boolean,
  disableStartTooltip?: string,
): string {
  if (uploading) return 'Aguarde o envio dos arquivos antes de iniciar o PRD';
  if (disableStart && disableStartTooltip) return disableStartTooltip;
  return '';
}

export interface PrdBriefingComposerProps {
  activityId: string;
  description: string;
  onDescriptionChange: (value: string) => void;
  files: Artifact[];
  onUpload: (file: File) => Promise<void>;
  onDelete?: (fileId: string) => Promise<void>;
  workerConnected: boolean;
  onStart: () => void;
  disableStart: boolean;
  disableStartTooltip?: string;
}

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    backgroundColor: theme.palette.background.paper,
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(3, 3.5, 2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
  },
  hero: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.5),
  },
  eyebrow: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.16em',
    textTransform: 'uppercase' as const,
    color: '#FA4616',
  },
  heroTitle: {
    fontFamily: "'Poppins', sans-serif",
    fontWeight: 600,
    fontSize: '1.25rem',
    letterSpacing: '-0.01em',
    color: theme.palette.text.primary,
    lineHeight: 1.3,
  },
  heroSubtitle: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.55,
    maxWidth: 640,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  fieldHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  fieldLabel: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase' as const,
    color: theme.palette.text.primary,
  },
  fieldHint: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    letterSpacing: '0.02em',
  },
  fieldHintRequired: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    color: '#FA4616',
  },
  textareaShell: {
    position: 'relative',
    borderRadius: 12,
    border: `1.5px solid #E0E0E0`,
    backgroundColor: '#FFFFFF',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      borderColor: '#BDBDBD',
    },
    '&:focus-within': {
      borderColor: '#FA4616',
      boxShadow: '0 0 0 4px rgba(250, 70, 22, 0.12)',
    },
  },
  textareaShellError: {
    borderColor: theme.palette.error.main,
    '&:hover': {
      borderColor: theme.palette.error.main,
    },
  },
  textarea: {
    width: '100%',
    minHeight: 160,
    maxHeight: 360,
    padding: theme.spacing(2, 2.5, 3.5),
    border: 'none',
    outline: 'none',
    resize: 'vertical' as const,
    fontFamily: "'Inter', sans-serif",
    fontSize: '0.9375rem',
    lineHeight: 1.65,
    color: theme.palette.text.primary,
    background: 'transparent',
    boxSizing: 'border-box' as const,
    display: 'block',
    '&::placeholder': {
      color: '#9E9E9E',
      fontStyle: 'italic' as const,
    },
  },
  textareaMeta: {
    position: 'absolute',
    bottom: 8,
    right: 14,
    fontFamily: "'Inter', sans-serif",
    fontSize: '0.68rem',
    color: theme.palette.text.secondary,
    pointerEvents: 'none' as const,
    letterSpacing: '0.03em',
  },
  textareaMetaWarn: {
    color: theme.palette.error.main,
    fontWeight: 600,
  },
  uploadZone: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.75, 2),
    borderRadius: 12,
    border: `1.5px dashed #D9D9D6`,
    backgroundColor: 'rgba(250, 70, 22, 0.03)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: '#FA4616',
      backgroundColor: 'rgba(250, 70, 22, 0.06)',
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 10px rgba(250, 70, 22, 0.08)',
    },
    '&:focus-visible': {
      outline: 'none',
      borderColor: '#FA4616',
      boxShadow: '0 0 0 4px rgba(250, 70, 22, 0.12)',
    },
  },
  uploadZoneActive: {
    borderStyle: 'solid',
    borderColor: '#FA4616',
    backgroundColor: 'rgba(250, 70, 22, 0.08)',
    transform: 'translateY(-1px)',
  },
  uploadZoneDisabled: {
    cursor: 'not-allowed',
    opacity: 0.6,
    '&:hover': {
      transform: 'none',
      boxShadow: 'none',
      borderColor: '#D9D9D6',
      backgroundColor: 'rgba(250, 70, 22, 0.03)',
    },
  },
  uploadIconTile: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(250, 70, 22, 0.12)',
    color: '#FA4616',
    flexShrink: 0,
  },
  uploadText: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },
  uploadTitle: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: '0.8125rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  uploadCaption: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    letterSpacing: '0.01em',
  },
  uploadButton: {
    flexShrink: 0,
    borderRadius: 8,
    fontFamily: "'Poppins', sans-serif",
    fontWeight: 500,
    fontSize: '0.75rem',
    padding: theme.spacing(0.75, 1.5),
    color: '#FA4616',
    border: `1px solid rgba(250, 70, 22, 0.3)`,
    backgroundColor: '#FFFFFF',
    '&:hover': {
      backgroundColor: 'rgba(250, 70, 22, 0.06)',
      borderColor: '#FA4616',
    },
  },
  fileChipsRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: theme.spacing(0.75),
    paddingTop: theme.spacing(0.5),
  },
  fileChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.5, 0.75, 0.5, 1),
    borderRadius: 999,
    border: `1px solid #E5E5E5`,
    backgroundColor: '#FFFFFF',
    fontFamily: "'Inter', sans-serif",
    fontSize: '0.72rem',
    color: theme.palette.text.primary,
    maxWidth: '100%',
    transition: 'border-color 0.15s ease, background-color 0.15s ease',
    '&:hover': {
      borderColor: '#FA4616',
      backgroundColor: 'rgba(250, 70, 22, 0.04)',
    },
  },
  fileChipIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
    '& svg': {
      fontSize: 14,
    },
  },
  fileChipName: {
    maxWidth: 180,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  fileChipSize: {
    color: theme.palette.text.secondary,
    fontSize: '0.66rem',
  },
  fileChipDelete: {
    padding: 2,
    marginLeft: 2,
    color: theme.palette.text.secondary,
    '&:hover': {
      color: '#FA4616',
      backgroundColor: 'rgba(250, 70, 22, 0.08)',
    },
  },
  errorBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.25),
    borderRadius: 8,
    border: `1px solid ${theme.palette.error.light}`,
    backgroundColor: 'rgba(244, 67, 54, 0.06)',
    color: theme.palette.error.dark,
    fontFamily: "'Inter', sans-serif",
    fontSize: '0.75rem',
  },
  infoNotice: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.25),
    borderRadius: 8,
    border: `1px dashed #D9D9D6`,
    backgroundColor: '#FAFAFA',
    color: theme.palette.text.secondary,
    fontFamily: "'Inter', sans-serif",
    fontSize: '0.72rem',
  },
  footer: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(1.75, 3.5),
    borderTop: `1px solid #F0F0F0`,
    backgroundColor: '#FAFAFA',
  },
  footerHint: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontFamily: "'Inter', sans-serif",
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    flex: 1,
    minWidth: 0,
  },
  footerHintReady: {
    color: '#1B5E20',
  },
  footerHintDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#BDBDBD',
    flexShrink: 0,
    transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
  },
  footerHintDotReady: {
    backgroundColor: '#43A047',
    boxShadow: '0 0 0 4px rgba(67, 160, 71, 0.18)',
  },
  startButton: {
    flexShrink: 0,
    borderRadius: 10,
    fontFamily: "'Poppins', sans-serif",
    fontWeight: 600,
    fontSize: '0.875rem',
    letterSpacing: '0.01em',
    padding: theme.spacing(1.1, 2.75),
    backgroundColor: '#FA4616',
    color: '#FFFFFF',
    boxShadow: '0 4px 14px rgba(250, 70, 22, 0.28)',
    transition:
      'transform 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease',
    '&:hover': {
      backgroundColor: '#E03D12',
      boxShadow: '0 6px 18px rgba(250, 70, 22, 0.36)',
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: '0 2px 8px rgba(250, 70, 22, 0.3)',
    },
    '&.Mui-disabled': {
      backgroundColor: '#EEEEEE',
      color: '#9E9E9E',
      boxShadow: 'none',
    },
  },
}));

function getFileIcon(fileType?: string) {
  if (!fileType) return <AttachFileIcon fontSize="small" />;
  if (fileType === 'application/pdf')
    return <PictureAsPdfIcon fontSize="small" />;
  if (fileType.startsWith('image/')) return <ImageIcon fontSize="small" />;
  return <DescriptionIcon fontSize="small" />;
}

function formatFileSize(bytes?: number): string {
  if (bytes === null || bytes === undefined) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const PrdBriefingComposer = ({
  description,
  onDescriptionChange,
  files,
  onUpload,
  onDelete,
  workerConnected,
  onStart,
  disableStart,
  disableStartTooltip,
}: PrdBriefingComposerProps) => {
  const classes = useStyles();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadError, setUploadError] = useState<string>();

  const trimmedDescription = description.trim();
  const descriptionValid = trimmedDescription.length > 0;
  const charCount = description.length;
  const charCountWarn = charCount > MAX_DESCRIPTION_LENGTH * 0.9;
  const canStart = descriptionValid && !disableStart && !uploading;

  const handleFiles = useCallback(
    async (fileList: FileList | File[]) => {
      setUploadError(undefined);
      const filesToUpload = Array.from(fileList);

      for (const file of filesToUpload) {
        if (file.size > MAX_FILE_SIZE) {
          setUploadError(`"${file.name}" excede o limite de 5 MB`);
          return;
        }
      }

      setUploading(true);
      try {
        for (const file of filesToUpload) {
          await onUpload(file);
        }
      } catch (e) {
        setUploadError(
          `Erro ao enviar arquivo: ${
            e instanceof Error ? e.message : String(e)
          }`,
        );
      } finally {
        setUploading(false);
        if (inputRef.current) {
          inputRef.current.value = '';
        }
      }
    },
    [onUpload],
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleFiles(e.target.files);
      }
    },
    [handleFiles],
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragOver(false);
      if (!workerConnected) return;
      if (e.dataTransfer.files.length > 0) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [handleFiles, workerConnected],
  );

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (!workerConnected) return;
      setDragOver(true);
    },
    [workerConnected],
  );

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleZoneClick = useCallback(() => {
    if (!workerConnected || uploading) return;
    inputRef.current?.click();
  }, [uploading, workerConnected]);

  const handleZoneKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleZoneClick();
      }
    },
    [handleZoneClick],
  );

  const hintLabel = (() => {
    if (!descriptionValid) {
      return 'Descreva a atividade para habilitar o Iniciar PRD.';
    }
    if (disableStart && disableStartTooltip) {
      return disableStartTooltip;
    }
    if (uploading) {
      return 'Aguarde o envio dos arquivos antes de iniciar o PRD.';
    }
    return 'Pronto para iniciar o PRD.';
  })();

  return (
    <div className={classes.root}>
      <div className={classes.body}>
        <div className={classes.hero}>
          <span className={classes.eyebrow}>Briefing · Fase PRD</span>
          <Typography component="h2" className={classes.heroTitle}>
            Defina o que você quer construir
          </Typography>
          <Typography className={classes.heroSubtitle}>
            Descreva a atividade com o máximo de detalhes e, se quiser, anexe
            arquivos de contexto. O agente usará essas informações para gerar o
            PRD.
          </Typography>
        </div>

        <div className={classes.field}>
          <div className={classes.fieldHeader}>
            <span className={classes.fieldLabel}>Descrição da atividade</span>
            <span
              className={
                descriptionValid
                  ? classes.fieldHint
                  : classes.fieldHintRequired
              }
            >
              {descriptionValid ? 'Preenchido' : 'Obrigatório'}
            </span>
          </div>
          <div
            className={`${classes.textareaShell} ${
              !descriptionValid && charCount > 0
                ? classes.textareaShellError
                : ''
            }`}
          >
            <textarea
              aria-label="Descrição da atividade"
              className={classes.textarea}
              placeholder="Ex.: Trocar a cor do texto dos cabeçalhos para o laranja Spassu em toda a aplicação, mantendo contraste AA..."
              value={description}
              onChange={e =>
                onDescriptionChange(
                  e.target.value.slice(0, MAX_DESCRIPTION_LENGTH),
                )
              }
              maxLength={MAX_DESCRIPTION_LENGTH}
            />
            <span
              className={`${classes.textareaMeta} ${
                charCountWarn ? classes.textareaMetaWarn : ''
              }`}
            >
              {charCount}/{MAX_DESCRIPTION_LENGTH}
            </span>
          </div>
        </div>

        <div className={classes.field}>
          <div className={classes.fieldHeader}>
            <span className={classes.fieldLabel}>Arquivos de contexto</span>
            <span className={classes.fieldHint}>
              {files.length > 0
                ? `${files.length} anexado${files.length > 1 ? 's' : ''}`
                : 'Opcional'}
            </span>
          </div>

          {workerConnected ? (
            <>
              <div
                className={`${classes.uploadZone} ${
                  dragOver ? classes.uploadZoneActive : ''
                } ${uploading ? classes.uploadZoneDisabled : ''}`}
                onClick={handleZoneClick}
                onKeyDown={handleZoneKeyDown}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                role="button"
                tabIndex={0}
                aria-label="Fazer upload de arquivos de contexto"
              >
                <div className={classes.uploadIconTile}>
                  {uploading ? (
                    <CircularProgress size={18} style={{ color: '#FA4616' }} />
                  ) : (
                    <CloudUploadIcon fontSize="small" />
                  )}
                </div>
                <div className={classes.uploadText}>
                  <span className={classes.uploadTitle}>
                    {dragOver
                      ? 'Solte para anexar'
                      : 'Arraste arquivos ou clique para selecionar'}
                  </span>
                  <span className={classes.uploadCaption}>
                    PDF · DOCX · MD · TXT · PNG · JPG · SVG — até 5 MB
                  </span>
                </div>
                <Button
                  className={classes.uploadButton}
                  onClick={e => {
                    e.stopPropagation();
                    handleZoneClick();
                  }}
                  disabled={uploading}
                  disableElevation
                >
                  {uploading ? 'Enviando...' : 'Selecionar'}
                </Button>
                <input
                  ref={inputRef}
                  type="file"
                  accept={ACCEPTED_EXTENSIONS}
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleInputChange}
                />
              </div>

              {files.length > 0 && (
                <div className={classes.fileChipsRow}>
                  {files.map(file => (
                    <span key={file.id} className={classes.fileChip}>
                      <span className={classes.fileChipIcon}>
                        {getFileIcon(file.fileType)}
                      </span>
                      <span className={classes.fileChipName}>
                        {file.fileName ?? 'Arquivo'}
                      </span>
                      <span className={classes.fileChipSize}>
                        {formatFileSize(file.fileSize)}
                      </span>
                      {onDelete && (
                        <Tooltip title="Remover">
                          <IconButton
                            size="small"
                            className={classes.fileChipDelete}
                            onClick={() => onDelete(file.id)}
                            aria-label={`Remover ${file.fileName ?? 'arquivo'}`}
                          >
                            <CloseIcon style={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </span>
                  ))}
                </div>
              )}

              {uploadError && (
                <div className={classes.errorBanner} role="alert">
                  {uploadError}
                </div>
              )}
            </>
          ) : (
            <div className={classes.infoNotice}>
              Conecte o worker para fazer upload de arquivos de contexto.
            </div>
          )}
        </div>
      </div>

      <div className={classes.footer}>
        <span
          className={`${classes.footerHint} ${
            canStart ? classes.footerHintReady : ''
          }`}
        >
          <span
            className={`${classes.footerHintDot} ${
              canStart ? classes.footerHintDotReady : ''
            }`}
            aria-hidden="true"
          />
          {hintLabel}
        </span>
        <Tooltip
          title={getStartTooltip(uploading, disableStart, disableStartTooltip)}
          arrow
        >
          <span>
            <Button
              className={classes.startButton}
              startIcon={<PlayArrowIcon />}
              onClick={onStart}
              disabled={!canStart}
              aria-label="Iniciar PRD"
            >
              Iniciar PRD
            </Button>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};
